import { type FC } from 'react';
import type { SimulationChoice, RiskLevel } from '@/types/simulation';

interface ChoicePanelProps {
  choices: SimulationChoice[];
  onChoose: (id: 'A' | 'B' | 'C') => void;
  disabled?: boolean;
  autoMode?: boolean;
}

const RISK_STYLES: Record<RiskLevel, { border: string; bg: string; badge: string; text: string }> = {
  low:      { border: 'border-emerald-700/60', bg: 'hover:bg-emerald-950/40', badge: 'bg-emerald-900/60 text-emerald-400', text: 'LOW RISK' },
  medium:   { border: 'border-yellow-700/60',  bg: 'hover:bg-yellow-950/40',  badge: 'bg-yellow-900/60 text-yellow-400',  text: 'MED RISK' },
  high:     { border: 'border-orange-700/60',  bg: 'hover:bg-orange-950/40',  badge: 'bg-orange-900/60 text-orange-400',  text: 'HIGH RISK' },
  critical: { border: 'border-red-600/60',     bg: 'hover:bg-red-950/50',     badge: 'bg-red-900/60 text-red-400',        text: 'CRITICAL' },
};

const CHOICE_ICONS: Record<string, string> = { A: '🅰', B: '🅱', C: '🆑' };

export const ChoicePanel: FC<ChoicePanelProps> = ({ choices, onChoose, disabled, autoMode }) => {
  if (choices.length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
        {autoMode ? '🤖 Auto-selecting next move...' : 'What happens next?'}
      </p>
      {choices.map((choice) => {
        const style = RISK_STYLES[choice.risk_level];
        return (
          <button
            key={choice.id}
            onClick={() => !disabled && onChoose(choice.id)}
            disabled={disabled}
            className={`w-full text-left rounded-lg border px-3 py-2.5 transition-all
              ${disabled ? 'opacity-50 cursor-not-allowed' : `cursor-pointer ${style.bg}`}
              ${style.border} ${autoMode && choice.id === 'A' ? 'ring-1 ring-amber-500/40' : ''}
            `}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-base leading-none shrink-0">{CHOICE_ICONS[choice.id]}</span>
                <span className="text-xs font-semibold text-slate-200 leading-snug">{choice.label}</span>
              </div>
              <span className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${style.badge}`}>
                {style.text}
              </span>
            </div>
            <p className="text-[10px] text-slate-500 mt-1 ml-7 leading-snug">{choice.consequence}</p>
          </button>
        );
      })}
    </div>
  );
};
